
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src2984367493/src/vl-app/pages/clinician/session.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { Alert, Modal, Button, Dropdown, Empty, Progress, Result, Select, Steps } from 'antd';
import { CheckSquareTwoTone, DownOutlined, StopTwoTone } from 'antd/icons';
import { Form } from 'ALIASED-antd';

import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import useUserNextAppointments from '@hooks/useUserNextAppointments';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import useUserAppointments from '@hooks/useUserAppointments';
import useCallStatus from '@hooks/useCallStatus';
import useMyStatusUpdater from '@hooks/useMyStatusUpdater';
import useAudio from '@hooks/useAudio';
import { getLogger } from 'loglevel';
import { useCancelledAppointments } from '@hooks/useCancelledAppointments';
import useAppointmentPrescriptions from '@hooks/useAppointmentPrescriptions';
import useMedicationsLoader from '@hooks/useMedicationsLoader';
import useMediaQuery from 'vl-common/src/hooks/useMediaQuery';

import Assessment from '@components/Clinicians/Assessment';
import useAppointmentGrabbed from '@hooks/useAppointmentGrabbed';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { alertModal, ConfirmationModal, ErrorModal, errorModal } from 'vl-common/src/components/Modals';
import useUser from '@vl-core/hooks/useUser';
import useVoiceCalling from '@hooks/useVoiceCalling';

import Header from '@components/Layouts/Header/Header';
import Tab from '@components/UI/Tabs/Tab';
import GPHealthRecord from '@components/Clinicians/Session/GPHealthRecord';
import ERSreferral from '@components/Clinicians/Session/ERSreferral';
import Documents from '@components/Clinicians/Session/Documents';
import { Actions } from '@components/Clinicians/Session/v2/Actions';
import DiagnosisAndOutcome from '@components/Clinicians/Session/v1/DiagnosisAndOutcome';
import LetterGeneration from '@components/Clinicians/Session/LetterGeneration';
import DataProtection from '@components/Clinicians/Session/DataProtection';
import ConfirmInvestigationModal from '@components/Clinicians/InvestigationRequest/ConfirmInvestigationModal';
import { useTranslation } from 'react-i18next';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useInvestigationRequestPDFUtils } from '@components/Clinicians/InvestigationRequest/InvestigationRequestModel';
import assert from 'assert';
import PatientInformation from '@components/Clinicians/PatientInformation';
import { SessionForm } from '@components/Clinicians/session-form';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useCleanUpAppointmentStorage, useSessionFormState } from '@hooks/useSessionFormState';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';
import { useDPACheck } from '@hooks/useDPACheck';
import type { Appointment } from 'vl-common/src/schemas/shared';
import Clinical from '@components/Clinicians/Session/Clinical';
import { AppointmentsMeta } from 'vl-common/src/schemas/getMetaAppointments';
import { useValidateActionTab } from '@hooks/useValidateActionTab';
import { useSessionTabs, TabEnum } from '@hooks/useSessionTabs';
import useSessionType from '@hooks/useSessionType';
import PatientStatusPanel from '@components/Clinicians/Session/PatientStatusPanel';
import { HeidiSessionProvider } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { breakpoints } from '@lib/breakpoints';
import CallMenu from '@components/Clinicians/Session/Components/CallMenu';
import ClinicianSession from '@components/Clinicians/Session/ClinicianSession';
import { useAppointmentLetterReview } from '@components/Clinicians/Session/hooks/useAppointmentLetterReview';
import useRuntimeConfig from '@vl-core/useRuntimeConfig';
import useAuth from '@vl-core/useAuth';
import { useValidateClinicalTab } from '@components/Clinicians/Session/hooks/useValidateClinicalTab';
import { useLeaveAppointmentConfirmation } from '@components/Clinicians/Session/hooks/useLeaveAppointmentConfirmation';
import useClinicianDiary from '@hooks/useClinicianDiary';
import CallPatient from '@components/Clinicians/CallPatient';
import SessionLoader from '@components/Clinicians/SessionLoader';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import { HeidiErrorProvider } from '@hooks/useHeidiError';
import useAppointmentEvents from '@hooks/useAppointmentEvents';

const InvestigationRequest = dynamic(() => import('@components/Clinicians/InvestigationRequest'), { ssr: false });

const log = getLogger(__filename);

const { Option } = Select;

const ClinicianSessionStyled = styled.div`
  display: flex;
  background-color: #f6f6f6;

  .session-view {
    padding: 1em;
    flex: 1;
    margin: 0 0 4em 0;
    width: 70%;

    @media (max-width: ${breakpoints.md}px) {
      margin: 0 0 8em 0;
    }
  }

  @media (max-width: ${breakpoints.md}px) {
    flex-direction: column;
  }

  .step-container {
    margin: 20px auto;
    .ant-steps-dot .ant-steps-item-content {
      min-width: 100px;

      @media (min-width: ${breakpoints.md}px) {
        min-width: 130px;
      }
    }

    .mobile-only {
      display: block;

      @media (min-width: 1024px) {
        display: none;
      }
    }

    .desktop-only {
      display: none;

      @media (min-width: 1024px) {
        display: block;
      }
    }
  }
`;

const AppointmentVersionEnum = {
  V1: 'V1',
  V2: 'V2'
};

function useMissingBlocksInLetterWarning(activeTab: string) {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const viewingLetter = activeTab === 'Letters';

  const appt_id = useSessionAppointmentId(true);

  const { data, status } = useQuery({
    queryKey: ['includeIntoLetter', appt_id],
    queryFn: () => dispatch(actions.getSelectedLetterTokens(appt_id)),
    enabled: !!appt_id,
    placeholderData: () => ({ appt_letter_tokens: [] }),
    keepPreviousData: true
  });

  useEffect(() => {
    if (viewingLetter && status === 'success') {
      const { appt_letter_tokens } = data;
      const missingTokens = appt_letter_tokens.filter(
        ({ show_in_letter, blank_value_warning }) => show_in_letter && blank_value_warning
      );

      if (missingTokens.length > 0) {
        alertModal({
          title: 'Missing fields',
          content: (
            <div>
              <p>{t('The letter template refers to missing information:')}</p>
              <ul>
                {missingTokens.map(({ token_code, token_description }) => (
                  <li key={token_code}>{token_description}</li>
                ))}
              </ul>
            </div>
          )
        });
      }
    }
  }, [viewingLetter, status]);
}

const OldClinicianSession = ({
  appointment,
  onAppointmentChange
}: {
  appointment: Appointment;
  onAppointmentChange: () => void;
}) => {
  const dispatch = useDispatch();
  const isMobileView = useMediaQuery('(max-width: 1023px)');
  const router = useRouter();
  const queryClient = useQueryClient();
  const user = useUser();
  const sessionAppointment = appointment;
  const [shownProtect, setShownProtect] = useState(0);
  const { t } = useTranslation('common');
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelReasonList, setCancelReasonList] = useState<AppointmentsMeta['appt_cancel_reasons']>();
  const [cancelReason, setCancelReason] = useState(false);
  const [cancelAppointmentCompleted, setCancelAppointmentCompleted] = useState(false);
  const [cancelAppointmentError, setCancelAppointmentError] = useState(false);
  const isCompleted = sessionAppointment.appointment_completed;
  const [appointmentOutcome] = useSessionFormState('appointment_outcome');
  const [caseModalVisible, setCaseModalVisible] = useState(false);
  const [, setModel] = useState(false);
  const [request] = useSessionFormState('request', false);
  const [, setSurveyId] = useState(false);
  const [ubrnActive, setUBRNActive] = useState(false);
  const [apptProgress, setApptProgress] = useState(0);
  const [extraTabsInReviewStep, setExtraTabsInReviewStep] = useState(0);
  const { tabs, setTabs, activeTab, setActiveTab } = useSessionTabs();
  const [openComplete, setOpenComplete] = useState(false);
  const [progress, setProgress] = useState(false);
  const [appointmentCompleting, setAppointmentCompleting] = useState(false);
  const [disableCompleteAppointmentButton, setDisableCompleteAppointmentButton] = useState(false);
  const [percent, setPercent] = useState(0);
  const [step, setStep] = useState('Posting Docs');
  const [alert, setAlert] = useSessionFormState('alert');
  const [letterViewed] = useSessionFormState('letterViewed', false);
  const [letterReq] = useSessionFormState('letterReq', {
    letter_code: '',
    letter_template_type: ''
  });
  const [f2fReferralPosted] = useSessionFormState('f2fReferralPosted', appointment?.referral_created);
  const refetchLetter = useRef(() => {});
  const [shouldRefetch, setShouldRefetch] = useState(0);
  const [tableMedications, setTableMedications] = useSessionFormState('tableMedications');

  const [virtualOnlyWarningTabs, setVirtualOnlyWarningTabs] = useState(() => ['Outcome', 'Requests', 'Actions']);

  const [, , stopVideoRinger] = useAudio('/static/telephone-ring.mp3', 0.6);

  const { invalidateCalendarEvents } = useClinicianDiary({ invalidateOnly: true });

  const { onCallPatient, hangupVoiceCall, audioCallUI, callPatientProps } = useVoiceCalling(
    sessionAppointment,
    stopVideoRinger,
    onAppointmentChange
  );
  useMissingBlocksInLetterWarning(activeTab);

  const validateClinicalTab = useValidateClinicalTab();
  const validateActionTab = useValidateActionTab();

  const cleanUpAppointmentStorage = useCleanUpAppointmentStorage();

  const { data: eligibilityChecks } = useEligibilityChecks();
  const isVirtualServicesOnly = !!eligibilityChecks?.VIRTUAL_ONLY;
  const [isDPACheck] = useSessionFormState('isDPACheck');
  const { detailsCurrentAppointment } = useAppointmentDetails(appointment.appointment_id);
  const showSecurityTab =
    isDPACheck || detailsCurrentAppointment?.audio_call_occurred || detailsCurrentAppointment?.video_call_occurred;
  const { validateDPACheck } = useDPACheck();

  const [showConfirmInvestigationModal, setShowConfirmInvestigationModal] = useState(false);
  const [confirmInvestigationData, setConfirmInvestigationData] = useState([]);
  const [refetchInvestigationRequests, setRefetchInvestigationRequests] = useSessionFormState(
    'refetchInvestigationRequests',
    0
  );
  const { loadPDFDocument, getMissingFields, flattenAndSave, loadInvestigationRequests } =
    useInvestigationRequestPDFUtils(appointment.appointment_id);

  const { data: appointmentMeta } = useAppointmentMeta();

  const { close: closeWS } = useAppointmentEvents(appointment);

  const validMedications = useCallback(() => {
    const bad = tableMedications?.find(({ duration, frequency }) => !duration?.trim() || !frequency?.trim());

    if (bad) {
      setAlert({
        type: 'error',
        message: `Please provide a "${bad.duration?.trim() ? 'Frequency' : 'Duration'}" for "${
          bad.vmp_name || bad.current_vmp?.vmp_name
        }"`
      });
    } else {
      setAlert(null);
    }

    return !bad;
  }, [tableMedications]);

  useAppointmentGrabbed(appointment);
  useEffect(() => {
    validMedications();
  }, [tableMedications, validMedications]);

  const security = useMemo(() => {
    if (showSecurityTab) {
      return <DataProtection />;
    }
    return null;
  }, [isDPACheck]);

  const documents = useMemo(
    () => (
      <Pad>
        <Documents />
      </Pad>
    ),
    []
  );

  const { prescribeMedication, canPrescribe } = useAppointmentPrescriptions();

  const actionTab = useMemo(() => {
    // V2 Actions Tab
    return (
      <Pad>
        <Actions />
      </Pad>
    );
  }, []);

  const outcomeTab = useMemo(() => {
    // V1 Actions Tab
    return (
      <Pad>
        <DiagnosisAndOutcome />
      </Pad>
    );
  }, [onAppointmentChange]);

  const clinicalTab = useMemo(
    // V2 Clinical
    () => (
      <Pad>
        <Clinical />
      </Pad>
    ),
    []
  );

  const investigationRequest = useMemo(() => {
    if (appointmentMeta?.requests_tab) {
      return (
        <Pad>
          <InvestigationRequest />
        </Pad>
      );
    }

    return (
      <Pad>
        <Empty image={null} description={t('No requests are available.')} />
      </Pad>
    );
  }, [appointment, isCompleted, appointment.read_only, refetchInvestigationRequests, appointmentMeta?.requests_tab]);

  const ersReferral = useMemo(
    () =>
      ubrnActive && (
        <Pad>
          <ERSreferral />
        </Pad>
      ),
    [sessionAppointment, ubrnActive, isCompleted]
  );

  const gpHealthRecord = useMemo(
    () =>
      appointment?.ods_code && (
        <Pad>
          <GPHealthRecord />
        </Pad>
      ),
    [sessionAppointment, isCompleted]
  );

  const [tabContents, setTabContents] = useState([gpHealthRecord, ersReferral, outcomeTab]);

  const { appointment_id, clinician_user_guid, patient_user_guid, status } = appointment;
  const appointmentIdentifiers = useMemo(
    () => ({ appointment_id, clinician_user_guid, patient_user_guid, status }),
    [appointment_id, clinician_user_guid, patient_user_guid, status]
  );
  const [, invalidateAppointments] = useUserNextAppointments(10);
  const { endCall, updatersReady } = useCallStatusUpdaters(appointmentIdentifiers);
  const callStatus = useCallStatus(appointmentIdentifiers);
  const sessionType = useSessionType();
  const { setCancelledAppointments } = useCancelledAppointments();

  const medicationsLoader = useMedicationsLoader(
    appointmentIdentifiers.clinician_user_guid,
    appointmentIdentifiers.appointment_id
  );

  useMyStatusUpdater(appointmentIdentifiers, appointment.read_only);

  appointment.patient_entered_at = new Date();

  const pdfSave = async (isPreview) => {
    const request = { preview: isPreview, ...letterReq };
    await dispatch(
      actions.saveLamdaPDF(
        appointment.clinician_user_guid,
        appointment.appointment_id,
        appointment.patient_user_guid,
        request
      )
    );
  };

  const onData = (data, id) => {
    setModel(data);
    setSurveyId(id);
  };

  useEffect(() => {
    async function checkUBRNActive() {
      const temp_ubrns_res = await dispatch(
        actions.getUBRNsFromCaseNotes(
          sessionAppointment.case_id,
          sessionAppointment.patient_user_guid,
          sessionAppointment.appointment_id
        )
      );
      const ubrnList = [];
      if (temp_ubrns_res.patient_notes) {
        temp_ubrns_res.patient_notes.forEach((note) => ubrnList.push(note.ubrn));
      }
      setUBRNActive(sessionAppointment.ubrn?.length > 0);
    }
    checkUBRNActive().then();
  }, [
    dispatch,
    sessionAppointment.appointment_id,
    sessionAppointment.case_id,
    sessionAppointment.patient_user_guid,
    sessionAppointment.ubrn?.length
  ]);

  const {
    isLoading: isLoadingAssessmentSurveys,
    data: assessmentSurveys,
    refetch: refetchSurveys
  } = useQuery({
    queryKey: ['getAssessmentSurveys', sessionAppointment.appointment_id],

    queryFn: async () =>
      dispatch(
        actions.getAssessmentSurveys({
          appt_id: sessionAppointment.appointment_id,
          response: 1
        })
      )
  });

  const appointmentType =
    Number(detailsCurrentAppointment?.appt_version) <= 1 || detailsCurrentAppointment?.appt_id === null
      ? AppointmentVersionEnum.V1
      : AppointmentVersionEnum.V2;

  const isAppointmentV1 = appointmentType === AppointmentVersionEnum.V1;
  const isAppointmentV2 = appointmentType === AppointmentVersionEnum.V2;

  const fetchSurveysAndUpdateTabs = useCallback(() => {
    if (!assessmentSurveys) return;
    const resAppt = assessmentSurveys;
    // By default, the extra tabs are Documents and Chat
    let extraTabsNumber = 1;

    let tabNames = [TabEnum.REQUESTS, TabEnum.ACTIONS, TabEnum.OUTCOME, TabEnum.LETTERS];
    const clinicalTabContent = appointmentMeta?.notes_tab ? [clinicalTab] : [];
    const requestsTabContent = isAppointmentV1 ? [investigationRequest] : [];
    let actionTabContent = null;

    const gpHealthRecordTabContent = appointment?.ods_code ? [gpHealthRecord] : [];
    if (appointmentMeta?.notes_tab) tabNames.unshift(TabEnum.CLINICAL);

    if (isAppointmentV1) {
      // Remove actions tab for v1 appointments
      tabNames = tabNames.filter((tab) => tab !== TabEnum.ACTIONS);
      actionTabContent = outcomeTab;
    }
    if (isAppointmentV2) {
      // Remove outcome and requests tabs for v2 appointments
      tabNames = tabNames.filter((tab) => tab !== TabEnum.OUTCOME && tab !== TabEnum.REQUESTS);
      actionTabContent = actionTab;
    }

    if (showSecurityTab) {
      if (!isMobileView) {
        extraTabsNumber += 1;
      }
      tabNames.unshift(TabEnum.SECURITY);
    }
    tabNames.unshift(TabEnum.DOCUMENTS);
    if (ubrnActive) {
      extraTabsNumber += 1;
      tabNames.unshift(TabEnum.ERS_REFERRAL);
    }
    if (appointment?.ods_code) {
      extraTabsNumber += 1;
      tabNames.unshift(TabEnum.GP_RECORD);
    }
    if (resAppt.type === actionTypes.ASSESSMENT_SURVEY_SUCCESS && resAppt.payload) {
      extraTabsNumber += resAppt.payload.length;
      setExtraTabsInReviewStep(extraTabsNumber);
      let surveyRess = [];
      if (resAppt.payload) surveyRess = [...surveyRess, ...resAppt.payload].filter(Boolean);
      const tabs = [
        ...surveyRess.map((item) => {
          const surveysWithSameName = surveyRess.filter(
            (survey) => survey.shortname === item.shortname || survey.title === item.title
          );

          if (surveysWithSameName.length > 1) {
            const indexOfCurrentSurveyWithSameName = surveysWithSameName.findIndex(
              (survey) => survey.surveyjs_id === item.surveyjs_id
            );
            return `${item.shortname || item.title} (${indexOfCurrentSurveyWithSameName + 1})`;
          }
          return item.shortname || item.title;
        }),
        ...tabNames
      ];

      const contents = [
        ...surveyRess.map((surveyRes) => (
          <Assessment
            key={surveyRes.surveyjs_id}
            case_review={appointment.case_review}
            pageType="clinician"
            surveyRes={surveyRes}
            onData={onData}
            refetch={async () => {
              // Will update surveys, which are a dependency of fetchSurveysAndUpdateTabs callback
              // And therefore will trigger useEffect below that calls fetchSurveysAndUpdateTabs
              await refetchSurveys();
            }}
            readonly={appointment.read_only}
            isLoadingSurveyRes={isLoadingAssessmentSurveys}
            apptCompleting={appointmentCompleting}
            showSurveyViewToggle
          />
        )),
        ...gpHealthRecordTabContent,
        ersReferral,
        documents,
        security,
        ...clinicalTabContent,
        ...requestsTabContent,
        actionTabContent
      ];
      setTabs(tabs);
      setActiveTab((tab) => tab || tabs[0]);
      setTabContents(contents);
    } else {
      setExtraTabsInReviewStep(extraTabsNumber);
      setTabs(tabNames);
      setTabContents([
        ...gpHealthRecordTabContent,
        ersReferral,
        documents,
        security,
        ...clinicalTabContent,
        ...requestsTabContent,
        actionTabContent
      ]);
    }
  }, [
    appointment,
    appointmentMeta,
    ubrnActive,
    assessmentSurveys,
    isCompleted,
    request,
    sessionAppointment,
    isDPACheck,
    canPrescribe,
    prescribeMedication,
    shouldRefetch,
    investigationRequest,
    appointmentCompleting,
    isAppointmentV1,
    isAppointmentV2
  ]);

  useEffect(() => {
    fetchSurveysAndUpdateTabs();
  }, [fetchSurveysAndUpdateTabs]);

  useEffect(() => {
    if (shownProtect === 1) {
      setShownProtect(2);
    }
  }, [shownProtect]);

  const endAppointment = async () => {
    const cancelReason = {
      user_guid: user.user_guid,
      appt_id: appointment.appointment_id.toString(),
      reason: 'DNA'
    };
    const reasonResponse = await dispatch(actions.rescheduleCancel(cancelReason));
    if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) {
      // flush the cached appointments so the AppointmentWaiting doesn't try accessing cancelled appointments
      invalidateAppointments();
      setCancelAppointmentCompleted(true);
    } else {
      setCancelAppointmentError(true);
    }
  };

  const onCancelVisibleModal = async (value, appointment) => {
    if (appointment.letter_saved || appointment.appointment_outcome_code !== null) {
      setCancelAppointmentError(true);
      return;
    }
    const responseMeta = await dispatch(actions.rescheduleCancelMeta());
    setCancelReasonList(responseMeta.payload[0].appt_cancel_reasons);
    setCancelReason({
      user_guid: user.user_guid,
      appt_id: appointment.appointment_id.toString()
    });
    setCancelModalVisible(value);
  };

  const onCaseVisibleModal = async (value, appointment) => {
    setCancelReason({
      user_guid: user.user_guid,
      appt_id: appointment.appointment_id.toString(),
      reason: 'APPT_NLR'
    });
    setCaseModalVisible(value);
  };

  const onReleaseAppointment = async () => {
    const reasonResponse = await dispatch(actions.rescheduleCancel(cancelReason));
    if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) {
      setCancelModalVisible(false);
      router.push('/clinician/appointments');
    } else {
      setCancelModalVisible(false);
    }
  };

  const onCancelAppointment = async () => {
    const reasonResponse = await dispatch(actions.rescheduleCancel(cancelReason));
    if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) {
      // flush the cached appointments so the AppointmentWaiting doesn't try accessing cancelled appointments
      invalidateAppointments();
      setCancelledAppointments((a) => ({ ...a, [appointment.appointment_id]: true }));
      setCancelModalVisible(false);
      setCancelAppointmentCompleted(true);
    } else {
      setCancelModalVisible(false);
      setCancelAppointmentError(true);
    }
  };

  const redirectAppointment = () => {
    router.push('/clinician/appointments');
  };

  const triggerRefetchInvRequests = () => {
    setRefetchInvestigationRequests((prevState) => prevState + 1);
  };

  const completedPDFRequest = async (ireq_id: number) => {
    const loaded = await loadPDFDocument(ireq_id);

    if (loaded.error) return false;

    const { meta, document } = loaded;

    if (!meta.edited_flag) {
      Modal.error({
        title: 'Requests have not been completed',
        content:
          'One or more requests have not been saved. Please fill in all required fields and save the changes, or delete the action.',
        width: 'auto',
        centered: true,
        onOk: () => {
          setActiveTab(TabEnum.ACTIONS);
        }
      });

      return false;
    }

    const missingRequiredFields = getMissingFields(document);

    if (missingRequiredFields.length > 0) {
      errorModal({
        title: 'An investigation request has not been completed',
        content: `An investigation request has required fields that are empty. Please complete them or delete the request. Fields: ${missingRequiredFields.join(', ')}.`,
        width: 'auto',
        centered: true,
        onOk: () => {
          setActiveTab(TabEnum.ACTIONS);
          setShouldRefetch(shouldRefetch + 1);
        }
      });

      return false;
    }

    return document;
  };

  /** Check all the investigation requests flatten. Returns {failedToSend: true} or {} */
  const prepareDocumentsOnCompleteAppointment = async () => {
    const imgDetailsRes = await loadInvestigationRequests(() => {});

    for (const { ireq_id } of imgDetailsRes) {
      const { error, document } = await loadPDFDocument(ireq_id);

      assert(!error);
      if (!(await flattenAndSave(document, ireq_id))) {
        return { failedToSend: true };
      }
    }

    return { failedToSend: false };
  };

  const complete = async () => {
    if (appointment.appointment_completed) {
      redirectAppointment();
    }
    setDisableCompleteAppointmentButton(true);

    const { failedToSend } = await prepareDocumentsOnCompleteAppointment();
    if (failedToSend) {
      // does this let the user know?
      setDisableCompleteAppointmentButton(false);
      return;
    }

    // it is possible to get to here without loading up the medications that were initially set up and then the browser
    // refreshed. So, sadly, need to try to load them now ...
    if (canPrescribe && prescribeMedication) {
      if (!tableMedications?.length) {
        const { medications } = await medicationsLoader();
        setTableMedications(medications);
      }
    }

    if (!validMedications()) {
      setDisableCompleteAppointmentButton(false);
      return;
    }

    const dpaCheckValid = await validateDPACheck();
    if (!dpaCheckValid) {
      setAppointmentCompleting(false);
      setDisableCompleteAppointmentButton(false);
      return;
    }

    const { isValid: clinicalTabValid, invalidMessage: clinicalTabInvalidMessage } = await validateClinicalTab();
    if (!clinicalTabValid) {
      setAppointmentCompleting(false);
      setDisableCompleteAppointmentButton(false);
      setAlert({
        type: 'error',
        message: clinicalTabInvalidMessage
      });
      return;
    }

    const { isValid: actionTabValid, invalidMessage: actionTabInvalidMessage } = validateActionTab();
    if (!actionTabValid) {
      setAppointmentCompleting(false);
      setDisableCompleteAppointmentButton(false);
      setAlert({
        type: 'error',
        message: actionTabInvalidMessage
      });
      return;
    }

    if (!appointment.appointment_outcome_code) {
      setAlert({
        type: 'error',
        message: 'Cannot complete an appointment without an outcome'
      });
      setDisableCompleteAppointmentButton(false);
      return;
    }

    const outcomeFound = appointmentMeta?.appt_outcomes?.find(
      (outcome) => outcome?.appt_outcome_code === appointment.appointment_outcome_code
    );
    // use appt_auth_f2f_flag
    if (outcomeFound?.appt_auth_f2f_flag) {
      if ((appointment.auth_granted || outcomeFound?.appt_auth_required_flag) && !f2fReferralPosted) {
        // if self book auth granted, check for f2f post referral
        setAlert({
          type: 'error',
          message: 'Please complete and save the "Face to face referral" fields in the "Outcome" tab'
        });
        setDisableCompleteAppointmentButton(false);
        return;
      }
    }
    setAppointmentCompleting(true);
    await pdfSave(false);
    setProgress(true);
    setPercent(30);

    const investigationRequests = await dispatch(actions.clinicianInvReqByApptId(1, appointment.appointment_id));
    const option = {
      user_guid: appointment.clinician_user_guid,
      invreqs: investigationRequests?.invreqs,
      appt_id: appointment.appointment_id
    };

    const doPrescribe = canPrescribe && prescribeMedication && tableMedications.length > 0;

    if (doPrescribe) {
      const { prescription_id } = await dispatch(actions.prescribeSignaturePharmacy(appointment.appointment_id));

      option.prescription_id = prescription_id;

      if (!prescription_id) {
        setAlert({
          type: 'error',
          message:
            'Sorry, unable to complete your appointment because of problems prescribing your medication. ' +
            'Either contact your administrator or remove the prescription and try again.'
        });
        setDisableCompleteAppointmentButton(false);
        setAppointmentCompleting(false);
        setProgress(false);
        return;
      }
    }

    const response = await dispatch(actions.completeSession({ ...option }), false);

    if (response.type === actionTypes.ACTION_FAIL) {
      response?.error?.json().then((err) => {
        setAlert({
          type: 'error',
          message: err?.message || `Unable to complete appointment. Status: ${response.status}`
        });
      });
      setDisableCompleteAppointmentButton(false);
      setAppointmentCompleting(false);
      setProgress(false);
      return;
    }

    await endCall();

    if (closeWS) {
      closeWS();
    }

    await hangupVoiceCall();

    const responseMap = {
      405: 'Cannot complete an appointment in the future',
      406: 'Member cannot be granted F2F booking auth'
    };

    if (response?.appt_id) {
      // reload the appointment, at least so that it is seen as being complete
      onAppointmentChange();
      setOpenComplete(false);

      cleanUpAppointmentStorage();
    } else {
      setAlert({
        type: 'error',
        message:
          response?.message ||
          responseMap?.[response?.status] ||
          'Trouble completing your appointment. Please contact your administrator'
      });
      setDisableCompleteAppointmentButton(false);
      setAppointmentCompleting(false);
    }

    // Invalidate the queries that populate the clinician's appointments tables
    queryClient.invalidateQueries({
      queryKey: ['clinicianAppointment']
    });
    invalidateCalendarEvents();

    setPercent(100);
    setProgress(false);
    setStep('Docs Uploaded');
    if (response?.appt_id) {
      setTimeout(() => redirectAppointment(), 1000);
    }
  };

  /** validates all IR's, flattens PDF IR's as required */
  const investigationRequestsReadyToSave = async (investigationRequests) => {
    function displayIncompleteIRError() {
      errorModal({
        title: 'An investigation request has not been completed',
        content: 'An investigation request has required fields that are empty. Please complete or delete the request.',
        width: 'auto',
        onOk: () => {
          setActiveTab(TabEnum.ACTIONS);
          setShouldRefetch(shouldRefetch + 1);
        }
      });

      return false;
    }

    for (const surveyIR of investigationRequests.filter((ir) => typeof ir.surveyjs_id === 'number' && ir.surveyjs_id)) {
      const investigationRequestSurveyResponse = await dispatch(actions.getSurveyJsId(surveyIR.surveyjs_id));
      const surveyIsComplete = investigationRequestSurveyResponse.user_surveyjs[0].completed_flag;

      if (!surveyIsComplete) {
        return displayIncompleteIRError();
      }
    }

    for (const surveyIR of investigationRequests.filter((ir) => !ir.surveyjs_id)) {
      const document = await completedPDFRequest(surveyIR.ireq_id);

      if (!document) return false;
    }

    return true;
  };

  /** Invoked by the "Complete" menu options. Triggers either:
   * - nothing: There's a problem with an investigation request
   * - ConfirmInvestigationModal (setShowConfirmInvestigationModal): Unexpected Request given the outcome
   * - ConfirmationModal (setOpenComplete): Warn the clinician that Requests are about to be saved
   *
   * Note: this doesn't flatten or save the PDF until after user confirmation has been secured (see complete())
   * */
  const checkInvestigationRequestsBeforeCompleting = useCallback(async () => {
    const displayIncompleteRequestError = () =>
      errorModal({
        title: 'An investigation request has not been completed',
        content:
          'An investigation request has required fields that are empty. Please complete them or delete the investigation request.'
      });

    if (!letterViewed) {
      setAlert({
        type: 'error',
        message: 'Review the letter before completing an appointment'
      });
      return;
    }

    setAlert(null);

    const investigationRequests = await loadInvestigationRequests(displayIncompleteRequestError);
    const currentAppointmentDetailsUpdated = await dispatch(actions.getAppointmentDetails(appointment.appointment_id));

    // investigationRequests length is 0 if at least one bad investigation request is added
    // no requests => no user confirmation
    if (investigationRequests.length === 0 && currentAppointmentDetailsUpdated.appts[0]?.invreq.length === 0) {
      setOpenComplete(true);
      return;
    }

    const outcomeFound = appointmentMeta?.appt_outcomes?.find(
      (outcome) => outcome?.appt_outcome_code === appointmentOutcome
    );

    const v2InvReqAction =
      isAppointmentV2 && outcomeFound?.appt_actions?.find((action) => action.appt_action_code === 'INVREQ');
    const v2OutcomeAllowsInvReq = v2InvReqAction && (v2InvReqAction.enabled || v2InvReqAction.mandatory);

    const investigationRequestExpected = isAppointmentV1 ? outcomeFound?.appt_ireq_flag : v2OutcomeAllowsInvReq;
    const requestsReady = await investigationRequestsReadyToSave(investigationRequests);

    if (!requestsReady) {
      return;
    }

    if (investigationRequestExpected) {
      setOpenComplete(true);
      return;
    }

    // Prevents displaying ConfirmInvestigationModal if there is error
    if (investigationRequests.length === 0 && currentAppointmentDetailsUpdated.appts[0]?.invreq.length !== 0) {
      return;
    }

    setConfirmInvestigationData({ investigationRequests, outcomeFound });
    setShowConfirmInvestigationModal(true);
  }, [
    isAppointmentV1,
    isAppointmentV2,
    appointment.appointment_id,
    appointmentMeta?.appt_outcomes,
    appointmentOutcome,
    dispatch,
    investigationRequestsReadyToSave,
    letterViewed,
    loadInvestigationRequests,
    setAlert
  ]);

  const endMenuItems = useMemo(
    () => ({
      caseReview: [
        {
          key: 'complete',
          icon: <CheckSquareTwoTone />,
          label: 'Complete',
          onClick: () => checkInvestigationRequestsBeforeCompleting()
        },
        {
          key: 'release',
          icon: <StopTwoTone twoToneColor="#eb2f96" />,
          label: 'Release',
          onClick: () => onCaseVisibleModal(true, sessionAppointment)
        }
      ],
      appointment: [
        {
          key: 'complete',
          icon: <CheckSquareTwoTone />,
          label: 'Complete',
          onClick: () => checkInvestigationRequestsBeforeCompleting()
        },
        {
          key: 'cancel',
          icon: <StopTwoTone twoToneColor="#eb2f96" />,
          label: 'Cancel',
          onClick: () => onCancelVisibleModal(true, sessionAppointment)
        },
        {
          key: 'dna',
          icon: <StopTwoTone twoToneColor="#eb2f96" />,
          label: 'DNA',
          onClick: () => endAppointment()
        }
      ]
    }),
    [
      endAppointment,
      onCancelVisibleModal,
      onCaseVisibleModal,
      checkInvestigationRequestsBeforeCompleting,
      sessionAppointment
    ]
  );

  const stepItems = useMemo(
    () =>
      [
        {
          title: (
            <Button type="link" data-testid="tab-Patient">
              {t('Patient')}
            </Button>
          ),
          key: tabs[0]
        },
        ...(isAppointmentV1
          ? [
              {
                title: <CallMenu onCallPatient={onCallPatient} />
              }
            ]
          : []),
        tabs.includes('Security') && {
          title: (
            <Button type="link" data-testid="tab-Security">
              {t('Security')}
            </Button>
          ),
          className: 'mobile-only',
          key: TabEnum.SECURITY
        },
        {
          title: (
            <Button type="link" data-testid="tab-Actions">
              {t('Assess')}
            </Button>
          ),
          key: tabs[0],
          className: 'mobile-only'
        },
        ...tabs.map((item, index) => {
          if (index < extraTabsInReviewStep || item === 'Documents') {
            return null;
          }
          return {
            title: (
              <Button type="link" data-testid={`tab-${item}`}>
                {item}
              </Button>
            ),
            className: 'desktop-only',
            key: item
          };
        }),
        {
          title: (
            <Dropdown
              menu={{
                items:
                  endMenuItems[
                    appointment.case_review && !appointment.appointment_completed ? 'caseReview' : 'appointment'
                  ]
              }}
              trigger={['click']}
            >
              <Button type="link" data-testid="end-appointment">
                {t('End ')}
                <DownOutlined />
              </Button>
            </Dropdown>
          ),
          status: 'wait'
        }
      ].filter((item) => item),
    [appointment, sessionType, tabs, endMenuItems, extraTabsInReviewStep, updatersReady]
  );

  // sync the steps with current tab when rejoining the appointment
  useEffect(() => {
    const stepIndex = stepItems.findIndex((item) => item.key === activeTab);

    setApptProgress(stepIndex === -1 ? 0 : stepIndex);
  }, [activeTab, stepItems]);

  const onChangeStep = (step) => {
    if (!stepItems?.[step]?.key) {
      return;
    }

    setApptProgress(step);
    setActiveTab(stepItems[step].key);
  };

  const onChangeTabTest = (activeKey) => {
    setActiveTab(activeKey);
    if (activeKey === TabEnum.LETTERS) refetchLetter?.current();

    const progressIndex = stepItems.findIndex((item) => item.key === activeKey);

    if (progressIndex === -1) {
      setApptProgress(0);
      return;
    }

    setApptProgress(progressIndex);
  };

  const eligibilityWarningModal = () => {
    Modal.warning({
      title: 'Warning',
      centered: true,
      content: (
        <>
          <div dangerouslySetInnerHTML={{ __html: t('eligibilityWarningModalHeader') }} />
          <div dangerouslySetInnerHTML={{ __html: t('eligibilityWarningModalContent') }} />
        </>
      ),
      okButtonProps: { type: 'primary' }
    });
  };

  useLeaveAppointmentConfirmation({ callStatus });

  useEffect(() => {
    if (isVirtualServicesOnly && virtualOnlyWarningTabs.includes(activeTab)) {
      setVirtualOnlyWarningTabs((prevState) => prevState.filter((tab) => tab !== activeTab));
      eligibilityWarningModal();
    }

    return () => {
      Modal.destroyAll();
    };
  }, [activeTab, isVirtualServicesOnly]);

  const returnToAppointmentsPage = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['clinicianAppointment']
    });
    router.push('/clinician/appointments').then();
  }, [router, queryClient]);

  return (
    <>
      <Header pageType="clinician" pageTitle="Session" />
      {!letterViewed && alert && (
        <Alert
          css={css({ marginBottom: '1rem' })}
          message={alert.message}
          type={alert.type}
          closable
          afterClose={() => setAlert(null)}
          showIcon
        />
      )}
      <ClinicianSessionStyled>
        <PatientStatusPanel />
        <div className="session-view">
          <PatientInformation />
          {sessionType && !appointment.appointment_completed && (
            <div className="step-container">
              <Steps current={apptProgress} onChange={onChangeStep} progressDot items={stepItems} />
            </div>
          )}
          <div
            css={css`
              box-shadow: 0 0 6px #0000004d;
              background-color: #fff;
              border-radius: 10px 10px 0 0;
              overflow: hidden;

              .ant-tabs-nav {
                padding: 0 1em;
              }
            `}
          >
            <Suspense fallback={<SessionLoader message={t('Initialising appointment form...')} />}>
              <Tab
                activeKey={activeTab}
                changeActiveTab={onChangeTabTest}
                tabNames={tabs}
                tabContent={tabContents?.filter((tab) => tab) || []}
                useTabNamesAsKeys
                mountAllTabContents
              />
              {activeTab === TabEnum.LETTERS && (
                <Pad
                  css={css`
                    padding: 0;
                  `}
                >
                  <LetterGeneration />
                </Pad>
              )}
            </Suspense>
          </div>
        </div>
      </ClinicianSessionStyled>
      {audioCallUI && <CallPatient {...callPatientProps} />}
      <ConfirmationModal
        open={cancelModalVisible}
        onCancel={() => setCancelModalVisible(false)}
        onOk={() => onCancelAppointment()}
        okText="Confirm"
        title="Cancel Appointment"
      >
        <Form.Item
          name="cancellation_reason"
          label={t('Why are you cancelling this appointment?')}
          labelCol={{ span: 24 }}
        >
          <Select
            placeholder={t('Select')}
            onChange={(value) => setCancelReason({ ...cancelReason, reason: value })}
            data-testid="appointment-cancel-reason-select"
          >
            {cancelReasonList?.map((item, index) => (
              <Option key={item.appt_cancel_desc} value={item.appt_cancel_code}>
                {item.appt_cancel_desc}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </ConfirmationModal>
      <ConfirmationModal
        open={caseModalVisible}
        onCancel={() => setCaseModalVisible(false)}
        onOk={() => onReleaseAppointment()}
        okText="Confirm"
        title="Release Case?"
      >
        {t('Releasing the case will delete any clinician-added data for the case')}
      </ConfirmationModal>
      <ConfirmationModal open={cancelAppointmentCompleted} footer={null} closable={false}>
        <Result
          status="info"
          title="Appointment Cancelled"
          subTitle="This appointment has now been cancelled and the member will be contacted by our admin team. A rescheduled appointment may be created when checks have been created"
          data-testid="appointment-cancelled-modal"
          extra={[
            <Button type="primary" key="console" onClick={returnToAppointmentsPage} data-testid="go-to-appointments">
              {t('Go to appointments')}
            </Button>
          ]}
        />
      </ConfirmationModal>
      <ErrorModal
        open={cancelAppointmentError}
        footer={null}
        onCancel={() => setCancelAppointmentError(false)}
        title="Unable to cancel appointment"
        description={t('There was an error cancelling this appointment.')}
        data-testid="ok"
      />
      <ConfirmationModal
        open={openComplete}
        closable={false}
        onOk={() => complete()}
        okText="Yes"
        onCancel={() => setOpenComplete(false)}
        okButtonProps={{
          disabled: !!alert?.message || disableCompleteAppointmentButton,
          loading: disableCompleteAppointmentButton
        }}
        cancelButtonProps={{
          disabled: disableCompleteAppointmentButton
        }}
        title="Complete appointment?"
        maskClosable={false}
      >
        {t('Completing the appointment will send all relevant document to the Patient’s Port')}
        {alert && (
          <Alert
            className="login-alert"
            message={alert.message}
            type={alert.type}
            afterClose={() => setAlert(null)}
            showIcon
          />
        )}
      </ConfirmationModal>
      <ConfirmationModal footer={false} closable={false} open={progress} title={step}>
        <Progress percent={percent} />
      </ConfirmationModal>
      <ConfirmInvestigationModal
        visible={showConfirmInvestigationModal}
        onCancel={() => setShowConfirmInvestigationModal(false)}
        investigations={confirmInvestigationData?.investigationRequests}
        appointmentOutcome={confirmInvestigationData?.outcomeFound}
        appointment={appointment}
        triggerRefetchInvRequests={triggerRefetchInvRequests}
        complete={complete}
        disableCompleteAppointmentButton={disableCompleteAppointmentButton}
        alert={alert}
        setAlert={setAlert}
      />
    </>
  );
};

const Pad = styled.div`
  padding: 0 1em 1em 1em;
`;

const LegacyClinicianSessionWrapper = () => {
  const { t } = useTranslation('common');
  const loggedInAndInitialised = useInitialisedStore();
  const router = useRouter();
  const qs = new URLSearchParams(router.query);
  const { user } = useAuth();

  const appt_id = useSessionAppointmentId(true);
  const { userAppointments: appointments, refetchUserAppointments: refresh } = useUserAppointments(
    'USER_APPOINTMENTS',
    { appt_id, team: qs.has('team') },
    { refetchOnWindowFocus: false, enabled: !!user && qs.has('req') }
  );

  const appointment = appointments ? appointments?.payload?.[0] : null;
  const [form] = Form.useForm<SessionForm>();

  const invalidateAppointmentLetterReview = useAppointmentLetterReview(form);

  if (!loggedInAndInitialised || !appointment) {
    return <p>{t('Initialising ...')}</p>;
  }

  return (
    <Form form={form} onValuesChange={invalidateAppointmentLetterReview} component={false}>
      <HeidiErrorProvider>
        <HeidiSessionProvider>
          <OldClinicianSession
            key={appointment.appointment_id}
            appointment={appointment}
            onAppointmentChange={refresh}
          />
        </HeidiSessionProvider>
      </HeidiErrorProvider>
    </Form>
  );
};

const ClinicianSessionWrapper = () => {
  const [form] = Form.useForm<SessionForm>();
  const { query } = useRouter();
  const router = useRouter();
  const appt_id = Number(Buffer.from(`${query.req}`, 'base64').toString());
  const { userAppointments: appointments } = useUserAppointments('USER_APPOINTMENTS', {
    appt_id,
    team: !!query.team
  });
  const loggedInAndInitialised = useInitialisedStore();
  const { APPT_SESSION_VERSION } = useRuntimeConfig();
  const appointment = appointments ? appointments?.payload?.[0] : undefined;
  const { detailsCurrentAppointment } = useAppointmentDetails(
    appointment?.audit_appt_id || appointment?.appointment_id
  );
  if (loggedInAndInitialised && router.pathname === '/clinician/session' && !appt_id) router.push('/clinician').then();

  if (loggedInAndInitialised && appointments?.type === actionTypes.USER_APPOINTMENTS_FAIL) {
    log.log('Redirecting to /clinician because of failed USER_APPOINTMENTS');
    router.push('/clinician').then();
  }

  // this suffers from painful React double mount issues
  const adobeSDKLoaded = useRef(false);
  useEffect(() => {
    if (loggedInAndInitialised && appointment && !('adobe_dc_sdk' in window) && !adobeSDKLoaded.current) {
      const adobeSDKLink = document.createElement('script');
      adobeSDKLink.setAttribute('src', 'https://documentservices.adobe.com/view-sdk/viewer.js');
      document.head.appendChild(adobeSDKLink);
      adobeSDKLoaded.current = true;
    }
  }, [loggedInAndInitialised, appointment]);

  const invalidateAppointmentLetterReview = useAppointmentLetterReview(form);

  if (appointment === undefined) {
    return (
      <ErrorModal
        onCancel={() => router.push('/clinician').then()}
        cancelText="Return to dashboard"
        open
        title="You do not have access to this appointment"
        data-testid="return-to-dashboard"
      />
    );
  }
  const appointmentType =
    Number(detailsCurrentAppointment?.appt_version) <= 1 || detailsCurrentAppointment?.appt_id === null
      ? AppointmentVersionEnum.V1
      : AppointmentVersionEnum.V2;
  const CURRENT_SESSION_VERSION = APPT_SESSION_VERSION || 1; // This is a temp constant to indicate the session version, this should be placed into a config file
  // const CURRENT_SESSION_VERSION = 2; // Use this in DEV for V2 Clinician Session
  // 1 - Legacy
  // 2 - New
  if (appointmentType === AppointmentVersionEnum.V2 && CURRENT_SESSION_VERSION === 2) {
    return (
      <Form form={form} onValuesChange={invalidateAppointmentLetterReview} component={false}>
        <Header pageType="clinician" pageTitle="Session" />
        <ClinicianSession key={`session-${appt_id}`} />
      </Form>
    );
  }
  return <LegacyClinicianSessionWrapper />;
};

const SuspensefulClinicianSession = () => {
  const { t } = useTranslation('common');
  return (
    <Suspense fallback={<SessionLoader message={t('Loading appointment page...')} />}>
      <ClinicianSessionWrapper />
    </Suspense>
  );
};

export default SuspensefulClinicianSession;
export const Route = createFileRoute('/clinician/session')({ component: SuspensefulClinicianSession });
